import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['comparisonCharts', 'dataTable', 'comments']

  static values = {
    token: String,
    showVariables: Boolean,
    showRawData: Boolean
  }

  connect() {
    if (this.hasComparisonChartsTarget) {
      this.loadComparisonData()
    }

    if (this.showRawDataValue && this.hasDataTableTarget) {
      this.loadRawData()
    }

    if (this.hasCommentsTarget) {
      this.loadComments()
    }
  }

  async loadComparisonData() {
    try {
      const response = await fetch(`/api/v1/experiments/shares/${this.tokenValue}/comparison`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) throw new Error('Failed to load comparison data')

      const data = await response.json()
      this.renderComparisonCharts(data)
    } catch (error) {
      console.error('Error loading comparison:', error)
      this.comparisonChartsTarget.innerHTML = `
        <div class="text-center py-8 text-gray-500">
          <p>Unable to load comparison data.</p>
        </div>
      `
    }
  }

  renderComparisonCharts(data) {
    if (!data.comparison || data.comparison.metrics.length === 0) {
      this.comparisonChartsTarget.innerHTML = `
        <div class="text-center py-8 text-gray-500">
          <p>Not enough data to show comparison yet.</p>
        </div>
      `
      return
    }

    const metricsHtml = data.comparison.metrics
      .map((metric) => `
      <div class="p-4 border border-gray-200 bg-gray-50">
        <h4 class="font-medium mb-3">${this.formatMetricName(metric.metric_type)}</h4>
        <div class="grid grid-cols-3 gap-4 text-center">
          <div>
            <span class="block text-sm text-gray-500">Baseline</span>
            <span class="block text-lg font-bold">${metric.baseline_mean?.toFixed(2) || 'N/A'}</span>
          </div>
          <div>
            <span class="block text-sm text-gray-500">Experiment</span>
            <span class="block text-lg font-bold">${metric.experiment_mean?.toFixed(2) || 'N/A'}</span>
          </div>
          <div>
            <span class="block text-sm text-gray-500">Change</span>
            <span class="block text-lg font-bold ${this.getChangeClass(metric.change_percent)}">
              ${metric.change_percent > 0 ? '+' : ''}${metric.change_percent?.toFixed(1) || 0}%
            </span>
          </div>
        </div>
        ${metric.statistically_significant ?
    '<span class="inline-block mt-2 text-xs px-2 py-1 bg-green-100 text-green-800">Significant</span>' :
    ''}
      </div>
    `)
      .join('')

    this.comparisonChartsTarget.innerHTML = `
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        ${metricsHtml}
      </div>
    `
  }

  async loadRawData() {
    try {
      const response = await fetch(`/api/v1/experiments/shares/${this.tokenValue}/data`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) throw new Error('Failed to load data')

      const data = await response.json()
      this.renderDataTable(data.snapshots || [])
    } catch (error) {
      console.error('Error loading raw data:', error)
      this.dataTableTarget.innerHTML = `
        <p class="text-gray-500 text-center py-8">Unable to load data points.</p>
      `
    }
  }

  renderDataTable(snapshots) {
    if (snapshots.length === 0) {
      this.dataTableTarget.innerHTML = `
        <p class="text-gray-500 text-center py-8">No data points recorded yet.</p>
      `
      return
    }

    // Group by date
    const byDate = {}
    snapshots.forEach((snapshot) => {
      const date = snapshot.snapshot_date
      if (!byDate[date]) byDate[date] = {}
      byDate[date][snapshot.metric_type] = snapshot.value
    })

    // Get all unique metric types
    const metricTypes = [...new Set(snapshots.map((s) => s.metric_type))]

    const headerHtml = `
      <th class="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase">Date</th>
      ${metricTypes.map((m) => `<th class="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase">${this.formatMetricName(m)}</th>`).join('')}
    `

    const rowsHtml = Object.entries(byDate)
      .sort(([a], [b]) => b.localeCompare(a))
      .slice(0, 30) // Show last 30 days
      .map(([date, values]) => `
        <tr class="border-t border-gray-200">
          <td class="px-3 py-2 text-sm">${this.formatDate(date)}</td>
          ${metricTypes.map((m) => `<td class="px-3 py-2 text-sm">${values[m]?.toFixed(2) || '-'}</td>`).join('')}
        </tr>
      `)
      .join('')

    this.dataTableTarget.innerHTML = `
      <div class="overflow-x-auto">
        <table class="min-w-full">
          <thead class="bg-gray-50">
            <tr>${headerHtml}</tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </div>
    `
  }

  async loadComments() {
    // Comments would be loaded from an API endpoint
    // For now, show a placeholder
    this.commentsTarget.innerHTML = `
      <p class="text-gray-500 text-center py-4">No comments yet. Be the first to comment!</p>
    `
  }

  async addComment(event) {
    event.preventDefault()

    const form = event.target
    const formData = new FormData(form)

    try {
      const response = await fetch(`/api/v1/experiments/shares/${this.tokenValue}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          author_name: formData.get('author_name'),
          content: formData.get('content')
        })
      })

      if (!response.ok) throw new Error('Failed to post comment')

      form.reset()
      this.showNotification('Comment posted!', 'success')
      this.loadComments()
    } catch (error) {
      console.error('Error posting comment:', error)
      this.showNotification('Failed to post comment', 'error')
    }
  }

  getChangeClass(changePercent) {
    if (changePercent > 0) return 'text-green-600'
    if (changePercent < 0) return 'text-red-600'
    return 'text-gray-600'
  }

  formatMetricName(metricType) {
    return metricType
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  formatDate(dateStr) {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  showNotification(message, type = 'success') {
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 z-50 p-4 border-2 ${
      type === 'success' ? 'bg-green-50 border-green-500 text-green-700' : 'bg-red-50 border-red-500 text-red-700'
    }`
    notification.textContent = message

    document.body.appendChild(notification)

    setTimeout(() => {
      notification.remove()
    }, 3000)
  }
}
